import React, { useState, useEffect } from 'react';
import { User, Mail, Building, Briefcase, CheckCircle, Loader2, Dices } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { AuthentipetLogo } from '../../../components/AuthentipetLogo';
import { AuthLayout } from './AuthLayout';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Link } from 'react-router-dom';
import { APP_CONSTANTS } from '../../../constants';
import { dataService } from '../../../services/dataService';
import { PropertyManagementCompany, UserRole } from '../../../types';
import { logger } from '../../../lib/logger';
import { validateEmail, authService } from '../../../services/authService';
import diceCaptcha from '../../../assets/dice-captcha.jpg';

interface SignUpProps {
  onSignUp: (email: string) => void;
}

export const SignUp: React.FC<SignUpProps> = ({ onSignUp }) => {
  logger.debug('SignUp component rendering');
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState<UserRole>('COMPANY_OWNER');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    companyName?: string;
    captcha?: string;
  }>({});
  const [mounted, setMounted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Company Dropdown State
  const [companies, setCompanies] = useState<Partial<PropertyManagementCompany>[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Partial<PropertyManagementCompany>[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  useEffect(() => {
    setMounted(true);
    // Fetch all companies for the dropdown
    const fetchCompanies = async () => {
      try {
        logger.debug('Fetching companies');
        const allCompanies = await dataService.getAllCompanies();
        logger.debug('Fetched companies', { count: allCompanies.length });
        setCompanies(allCompanies);
      } catch (error: unknown) {
        logger.error('Failed to fetch companies', error);
      }
    };
    fetchCompanies();
  }, []);

  // Filter companies when input changes
  useEffect(() => {
    if (companyName) {
      const filtered = companies.filter(c =>
        c.name?.toLowerCase().includes(companyName.toLowerCase())
      );
      setFilteredCompanies(filtered);
    } else {
      setFilteredCompanies([]);
    }
  }, [companyName, companies]);

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendSuccess(false);
    try {
      await authService.resendMagicLink(email);
      setResendSuccess(true);
    } catch (err: unknown) {
      logger.error('Failed to resend email', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to resend confirmation email');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Validate inputs
    const errors: { name?: string; email?: string; companyName?: string; captcha?: string } = {};
    
    const trimmedName = name.trim();
    if (!trimmedName) {
      errors.name = 'Name is required';
    } else if (trimmedName.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      errors.email = emailValidation.errors.join(', ');
    }

    const trimmedCompany = companyName.trim();
    if (!trimmedCompany) {
      errors.companyName = 'Company name is required';
    }

    // Only Company Owners can create new companies
    // Regional Managers and Property Managers must select an existing company
    if (!selectedCompanyId && (role === 'REGIONAL_MANAGER' || role === 'PROPERTY_MANAGER')) {
      errors.companyName = 'Please contact the owner of your company to create a company account.';
    }

    // Check basic field errors first
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Validate CAPTCHA via Edge Function (answer stays server-side)
    if (!captchaAnswer.trim()) {
      setFieldErrors({ captcha: 'Please enter your answer' });
      return;
    }

    setIsLoading(true);

    try {
      const captchaResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-captcha`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ answer: captchaAnswer.trim() }),
        }
      );

      const captchaResult = await captchaResponse.json();

      if (!captchaResult.valid) {
        setFieldErrors({ captcha: 'Incorrect answer. Look carefully at the dice!' });
        setIsLoading(false);
        return;
      }
    } catch {
      setError('Failed to validate CAPTCHA. Please try again.');
      setIsLoading(false);
      return;
    }

    // CAPTCHA passed - continue with signup
    try {
      let finalCompanyId = selectedCompanyId;
      const normalizedEmail = email.trim().toLowerCase();

      // 1. Create the company FIRST if not selected from dropdown
      if (!finalCompanyId) {
        const createdCompany = await dataService.createCompany(trimmedCompany, trimmedName, normalizedEmail);
        if (!createdCompany || !createdCompany.id) {
          throw new Error('Company creation failed');
        }
        finalCompanyId = createdCompany.id;
      }

      // 2. Sign up with magic link (passwordless)
      await authService.signUpWithOtp(normalizedEmail, {
        name: trimmedName,
        role,
        company_id: finalCompanyId!,
      });

      logger.info('Magic link signup sent', { email: normalizedEmail, companyId: finalCompanyId });
      setIsSuccess(true);
    } catch (err: unknown) {
      logger.error('Sign up error', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to create account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthLayout className="transition-colors duration-500">
        <div className={`sm:mx-auto sm:w-full sm:max-w-sm transition-all duration-700 transform translate-y-0 opacity-100`}>
          <div className="flex justify-center">
            <div className="bg-white/10 p-2 rounded-full backdrop-blur-sm shadow-xl">
              <AuthentipetLogo size={80} />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold font-league-spartan uppercase tracking-[-2px] text-storgia-teal drop-shadow-sm">
            Check Your Email!
          </h2>
          <div className={`mt-6 p-6 rounded-xl shadow-lg ${theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-100'}`}>
            <div className="text-center space-y-4">
              <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${theme === 'dark' ? 'bg-green-900/30' : 'bg-green-100'}`}>
                <CheckCircle className={`h-6 w-6 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`} />
              </div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                We&apos;ve sent a magic link to <strong>{email}</strong>.
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Click the link in the email to complete your registration and sign in.
              </p>
              <p className={`text-sm font-medium ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                Check your spam folder if you don&apos;t see it!
              </p>
              {resendSuccess && (
                <p className={`text-sm font-medium ${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                  ✓ Email resent successfully!
                </p>
              )}
              <div className="pt-4 space-y-2">
                <Button
                  onClick={handleResendEmail}
                  disabled={isResending}
                  variant="outline"
                  className="w-full"
                >
                  {isResending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Resending...
                    </span>
                  ) : (
                    'Resend Email'
                  )}
                </Button>
                <Button
                  onClick={() => onSignUp(email)}
                  className="w-full bg-storgia-cta-pink hover:bg-storgia-cta-pink/90 text-white shadow-[0_0_20px_rgba(192,0,84,0.3)]"
                >
                  Back to Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout className="transition-colors duration-500">
      <div className={`sm:mx-auto sm:w-full sm:max-w-sm transition-all duration-700 transform ${mounted ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'}`}>
        <div className="flex justify-center">
          <div className="bg-white/10 p-2 rounded-full backdrop-blur-sm shadow-xl">
            <AuthentipetLogo size={80} />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold font-league-spartan uppercase tracking-[-2px] text-storgia-teal drop-shadow-sm">
          Create Account
        </h2>
        <p className={`mt-2 text-center text-sm ${theme === 'dark' ? 'text-storgia-light-blurple/80' : 'text-gray-600'}`}>
          Start managing your portfolio today
        </p>
      </div>

      <div className={`mt-10 sm:mx-auto sm:w-full sm:max-w-sm transition-all duration-700 delay-100 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="p-3 rounded bg-red-500/10 border border-red-500/20 text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <div className={`transition-all duration-500 delay-200 transform ${mounted ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            <Input
              label="Full Name"
              id="name"
              name="name"
              type="text"
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: undefined }));
              }}
              placeholder="Jane Doe"
              leftIcon={<User className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
              error={fieldErrors.name}
            />
          </div>

          <div className={`transition-all duration-500 delay-300 transform ${mounted ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            <Input
              label="Email address"
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }));
              }}
              placeholder="owner@management.com"
              leftIcon={<Mail className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
              error={fieldErrors.email}
            />
          </div>

          <div className={`transition-all duration-500 delay-400 transform ${mounted ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            <div className="relative">
              <div className="flex items-center gap-2">
                  <span className={`block text-sm font-medium leading-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                    Company Name
                  </span>
                  <span className="text-xs font-bold text-storgia-cta-dark-pink uppercase">
                    THIS MUST BE SPELLED PERFECTLY
                  </span>
                </div>
              <Input
                label=""
                id="companyName"
                name="companyName"
                type="text"
                required
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setSelectedCompanyId(null); // Reset selection if typing
                  setIsDropdownOpen(true);
                  if (fieldErrors.companyName) setFieldErrors((prev) => ({ ...prev, companyName: undefined }));
                }}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="Search or create company..."
                leftIcon={<Building className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
                error={fieldErrors.companyName}
              />

              {/* Company Dropdown */}
              {isDropdownOpen && companyName.length > 0 && !selectedCompanyId && (
                <div className={`absolute z-50 w-full mt-1 rounded-md shadow-lg max-h-60 overflow-auto ${theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}>
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies.map((company) => (
                      <button
                        key={company.id}
                        type="button"
                        onClick={() => {
                          setCompanyName(company.name || '');
                          setSelectedCompanyId(company.id || null);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-storgia-blurple hover:text-white transition-colors ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                          }`}
                      >
                        <div className="font-medium">{company.name}</div>
                      </button>
                    ))
                  ) : (
                    <div className={`px-4 py-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      No matches found. &quot;{companyName}&quot; will be created.
                    </div>
                  )}

                  {/* Explicit "Create New" option if there are matches but user wants to create new */}
                  {filteredCompanies.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCompanyId(null);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm border-t ${theme === 'dark' ? 'border-gray-700 text-storgia-light-blurple hover:bg-gray-700' : 'border-gray-100 text-storgia-blurple hover:bg-gray-50'
                        }`}
                    >
                      <span className="font-bold">+</span> Create &quot;{companyName}&quot;
                    </button>
                  )}
                </div>
              )}

              {/* Selection Indicator */}
              {selectedCompanyId && (
                <div className="absolute right-3 top-[38px] text-green-500 pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          <div className={`transition-all duration-500 delay-450 transform ${mounted ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            <label htmlFor="role" className={`block text-sm font-medium leading-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
              Role
            </label>
            <div className="relative mt-2 rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Briefcase className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />
              </div>
              <select
                id="role"
                name="role"
                value={role}
                onChange={(e) => setRole(e.target.value as UserRole)}
                className={`block w-full rounded-md border-0 py-1.5 pl-10 ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${theme === 'dark'
                  ? 'bg-white/5 text-white ring-white/10 focus:ring-storgia-light-blurple'
                  : 'bg-white text-gray-900 ring-gray-300 focus:ring-storgia-blurple'
                  }`}
              >
                <option value="COMPANY_OWNER" className={theme === 'dark' ? 'bg-gray-800 text-white' : 'text-gray-900'}>Management Company Owner</option>
                <option value="REGIONAL_MANAGER" className={theme === 'dark' ? 'bg-gray-800 text-white' : 'text-gray-900'}>Regional Manager</option>
                <option value="PROPERTY_MANAGER" className={theme === 'dark' ? 'bg-gray-800 text-white' : 'text-gray-900'}>Property Manager</option>
              </select>
            </div>
          </div>

          {/* CAPTCHA */}
          <div className={`transition-all duration-500 delay-500 transform ${mounted ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'}`}>
              <p className={`text-sm font-medium mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Pick the dice pair with the same icon facing up
              </p>
              <img 
                src={diceCaptcha} 
                alt="Dice CAPTCHA - pick the pair with matching icons" 
                className="w-full rounded-md mb-3"
              />
              <Input
                label=""
                id="captcha"
                name="captcha"
                type="text"
                inputMode="numeric"
                pattern="[1-6]"
                maxLength={1}
                required
                value={captchaAnswer}
                onChange={(e) => {
                  setCaptchaAnswer(e.target.value);
                  if (fieldErrors.captcha) setFieldErrors((prev) => ({ ...prev, captcha: undefined }));
                }}
                placeholder="Enter number (1-6)"
                leftIcon={<Dices className={`h-5 w-5 ${theme === 'dark' ? 'text-storgia-light-blurple/50' : 'text-gray-400'}`} />}
                error={fieldErrors.captcha}
              />
            </div>
          </div>

          <div className={`transition-all duration-500 delay-700 transform ${mounted ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-storgia-cta-pink hover:bg-storgia-cta-pink/90 text-white shadow-[0_0_20px_rgba(192,0,84,0.3)] hover:shadow-[0_0_30px_rgba(192,0,84,0.5)] transition-all duration-300 transform hover:scale-[1.02]"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating Account...
                </span>
              ) : (
                'Sign up'
              )}
            </Button>
          </div>

          <p className={`text-center text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            We&apos;ll email you a magic link to complete registration — no password needed!
          </p>
        </form>

        <p className={`mt-10 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} transition-opacity duration-1000 delay-1000 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
          Already have an account?{' '}
          <Link to={APP_CONSTANTS.ROUTES.LOGIN} className={`font-semibold leading-6 ${theme === 'dark' ? 'text-storgia-green hover:text-storgia-green/80' : 'text-storgia-cta-pink hover:text-storgia-cta-pink/80'}`}>
            Sign in
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
};
